import React from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";
import "./index.css";

if (typeof window !== "undefined") {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  window.scrollTo(0, 0);
}

const root = createRoot(document.getElementById("root"));
const clerkPublishableKey =
  process.env.CLERK_PUBLISHABLE_KEY || "";

if (!clerkPublishableKey) {
  root.render(
    <div style={{ padding: "2rem", fontFamily: "system-ui, sans-serif" }}>
      <h1>Configuration error</h1>
      <p>
        Missing Clerk publishable key. Set
        <strong> CLERK_PUBLISHABLE_KEY </strong>
        in your webapp environment variables and redeploy.
      </p>
    </div>
  );
} else {
  root.render(
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <App />
    </ClerkProvider>
  );
}

const bootLoader = document.getElementById("boot-loader");
const bootLogo = document.querySelector(".boot-logo");
const bootTimers = [];
let bootLogoAnim = null;

const clearBootTimers = () => {
  while (bootTimers.length) {
    clearTimeout(bootTimers.pop());
  }
  if (bootLogoAnim) {
    bootLogoAnim.cancel();
    bootLogoAnim = null;
  }
};

const hideBootLoader = () => {
  if (!bootLoader) return;
  bootLoader.classList.add("is-hidden");
  document.body.classList.remove("boot-loading");
  document.documentElement.classList.remove("boot-loading");
  window.scrollTo(0, 0);
  window.dispatchEvent(new Event("bootloader:done"));
};

const playBootLoader = () => {
  if (!bootLoader) return;
  clearBootTimers();
  bootLoader.classList.remove("is-hidden");
  document.body.classList.add("boot-loading");
  document.documentElement.classList.add("boot-loading");
  window.scrollTo(0, 0);
  if (bootLogo) {
    bootLogo.classList.remove("is-visible");
    bootLogo.style.transform = "";
  }

  void bootLoader.offsetHeight;

  const typingDoneMs = 2450;
  const logoRevealDelay = 120;
  const moveDelay = 220;
  const moveDuration = 900;
  const fallbackHide = 4200;

  bootTimers.push(
    window.setTimeout(() => {
      if (!bootLogo) return;
      bootLogo.classList.add("is-visible");

      bootTimers.push(
        window.setTimeout(() => {
          const target =
            document.querySelector("[data-hero-logo]") ||
            document.querySelector(".fire-logo");
          if (!target) {
            bootLoader.classList.add("is-hidden");
            return;
          }
          const targetRect = target.getBoundingClientRect();
          const logoRect = bootLogo.getBoundingClientRect();
          const targetCenterX = targetRect.left + targetRect.width / 2;
          const targetCenterY = targetRect.top + targetRect.height / 2;
          const logoCenterX = logoRect.left + logoRect.width / 2;
          const logoCenterY = logoRect.top + logoRect.height / 2;
          const deltaX = targetCenterX - logoCenterX;
          const deltaY = targetCenterY - logoCenterY;
          const scale =
            logoRect.width > 0
              ? targetRect.width / logoRect.width
              : 1;

          bootLogoAnim = bootLogo.animate(
            [
              {
                transform:
                  "translate(-50%, -50%) translate(0px, 0px) scale(1)",
              },
              {
                transform: `translate(-50%, -50%) translate(${deltaX}px, ${deltaY}px) scale(${scale})`,
              },
            ],
            {
              duration: moveDuration,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)",
              fill: "forwards",
            }
          );
          bootLogoAnim.onfinish = hideBootLoader;
        }, moveDelay)
      );
    }, typingDoneMs + logoRevealDelay)
  );

  bootTimers.push(
    window.setTimeout(() => {
      hideBootLoader();
    }, fallbackHide)
  );
};

if (bootLoader) {
  const bootloaderPlayed =
    typeof window !== "undefined" &&
    window.sessionStorage?.getItem("bootloader-played");

  if (bootloaderPlayed) {
    hideBootLoader();
  } else {
    window.sessionStorage?.setItem("bootloader-played", "true");
    requestAnimationFrame(playBootLoader);
  }
}
